/* filter.js */

"use strict";

const _ = require("lodash");
const EventEmitter = require("events").EventEmitter;
const AdvancedFilter = require("../xDomeAdvancedFilterComponent");

const XDOME_TO_FILTER_TYPE_MAP = {
    string: "string",
    text: "string",
    longtext: "string",
    boolean: "boolean",
    integer: "number",
    decimal: "number",
    select: "enum",
    url: "string",
    date: "string",
    datetime: "string",
    json: "string",
};

const glu_to_xd_operators = {
    equal: "equals",
    not_equal: "not_equals",
    begins_with: "starts_with",
    not_begins_with: "not_starts_with",
    contains: "contains",
    not_contains: "not_contains",
    ends_with: "ends_with",
    not_ends_with: "not_ends_with",
    greater: "greater",
    greater_or_equal: "greater_or_equal",
    less: "less",
    less_or_equal: "less_or_equal",
    between: "between",
    not_between: "not_between",
    is_empty: "is_null",
    is_not_empty: "is_not_null",
};

const xd_to_glu_operators = Object.keys(glu_to_xd_operators).reduce((result, key) => {
    result[glu_to_xd_operators[key]] = key;
    return result;
}, {});

const ALLOWED_FILTER_TYPES = ["boolean", "string", "list", "number"];

class Filter extends EventEmitter {
    constructor(
        dataSyncConfigEntity,
        customFieldsWithInherited,
        deviceTypeBaseMap,
        osMap,
        vendorMap,
        sitePathMap,
        skuList,
        metaData
    ) {
        super();

        this._xdDataSyncConfigEntity = dataSyncConfigEntity;
        this._tabMainContent = undefined;
        this._xDomeDeviceInclusionFilter = undefined;
        this._customFieldsWithInherited = customFieldsWithInherited;
        this._osMap = osMap;
        this._vendorMap = vendorMap;
        this._sitePathMap = sitePathMap;
        this._skuList = skuList;
        this._deviceTypeBaseMap = deviceTypeBaseMap;
        this._metaData = metaData;

        this._inclusionFilterQueries = {
            xDomeInclusionFilter: {},
        };

        this._noConnectionEl = this._createNoConnectionPlaceholder();
        this._noMetaDataEl = this._createNoMetaDataPlaceholder();

        this._onInclusionFilterChanged = this._onInclusionFilterChanged.bind(this);
    }

    /*******************************************/
    /* START PRIVATE METHODS                   */
    /*******************************************/
    _createXDomeFilterOptions() {
        const xDomeDeviceProps = _.get(this._metaData, "xdome.deviceProps", {});
        const xDomeFilterOptions = [];
        for (let prop in xDomeDeviceProps) {
            if (xDomeDeviceProps[prop].filterable !== true) {
                continue;
            }
            xDomeFilterOptions.push({
                id: xDomeDeviceProps[prop].path[xDomeDeviceProps[prop].path.length - 1] || prop,
                name: xDomeDeviceProps[prop].path[xDomeDeviceProps[prop].path.length - 1] || prop,
                field: xDomeDeviceProps[prop].path[xDomeDeviceProps[prop].path.length - 1] || prop,
                label: xDomeDeviceProps[prop].displayName,
                dataType: xDomeDeviceProps[prop].type || "string",
                elementType: xDomeDeviceProps[prop].elementType || "string",
                // options: xDomeDeviceProps[prop].choices
            });
        }
        return xDomeFilterOptions;
    }

    _setInclusionFilters(inclusionFilters) {
        if (!this._xDomeDeviceInclusionFilter) {
            return;
        }
        let xDomeInclusionFilter = inclusionFilters.xDomeFilter || {};

        this._inclusionFilterQueries = {
            xDomeInclusionFilter,
        };
        const stdQuery = this._formatXDomeToStandard(
            this._inclusionFilterQueries.xDomeInclusionFilter
        );
        if (xDomeInclusionFilter && stdQuery.rules.length > 0) {
            this._xDomeDeviceInclusionFilter._renderer.$builderContainer.queryBuilder(
                "setRules",
                stdQuery
            );
        } else {
            this._xDomeDeviceInclusionFilter._renderer.$builderContainer.queryBuilder("reset");
        }
    }

    _resetInclusionFilters() {
        const namespaceMatchingCriteria = _.cloneDeep(
            _.get(this._xdDataSyncConfigEntity, "pristineProperties.namespaceMatchingCriteria", {})
        );

        namespaceMatchingCriteria.xDomeFilter = namespaceMatchingCriteria.xDomeFilter || {};

        this._setInclusionFilters(namespaceMatchingCriteria);
    }

    _createSection(content, options = {}) {
        const section = document.createElement("section");
        section.className = options.className || "";
        section.classList.toggle("bordered", !!options.bordered);

        if (options.title) {
            const title = document.createElement("h3");
            title.innerText = options.title;
            section.appendChild(title);
        }
        section.appendChild(content);
        return section;
    }

    _createAdvancedFilter(filterOptions) {
        return new AdvancedFilter({
            filterData: {},
            columnsConfig: this._getFilterOptions(filterOptions),
            allowGroups: 1,
        });
    }

    _createXDomeDeviceInclusionFilter() {
        const filterOptions = this._createXDomeFilterOptions();
        const customFields = _.get(this._metaData, "xdome.deviceCustomFields", []);
        let filterOption = null;
        let dataType = null;

        customFields.forEach((customField) => {
            if (
                customField.id &&
                customField.display &&
                Object.keys(XDOME_TO_FILTER_TYPE_MAP).indexOf(customField.data_type) >= 0
            ) {
                dataType = XDOME_TO_FILTER_TYPE_MAP[customField.data_type];

                filterOption = {
                    id: `customFieldValues.${customField.name}`,
                    field: `customFieldValues.${customField.name}`,
                    name: `customFieldValues.${customField.name}`,
                    label: customField.display,
                    dataType: dataType,
                    elementType: customField.element_type || "string",
                };

                if (dataType === "string" && !_.isEmpty(customField.choices)) {
                    if (_.isObject(customField.choices)) {
                        filterOption.options = Object.entries(customField.choices).map(
                            ([key, value]) => ({
                                value: key,
                                label: value,
                            })
                        );
                    } else {
                        filterOption.options = [];
                    }
                }

                filterOptions.push(filterOption);
            }
        });

        if (!filterOptions || filterOptions.length === 0) {
            this._noMetaDataEl.classList.add("show");
            this._tabMainContent.classList.add("no-connection");
            return document.createElement("div");
        } else {
            this._noMetaDataEl.classList.remove("show");
            this._tabMainContent.classList.remove("no-connection");
        }

        this._xDomeDeviceInclusionFilter = this._createAdvancedFilter(filterOptions);

        return this._xDomeDeviceInclusionFilter.getRootElement();
    }

    _getFilterOptions(filterOptions) {
        return filterOptions
            .filter((option) => {
                if (
                    option.dataType &&
                    !ALLOWED_FILTER_TYPES.includes(option.dataType.toLowerCase())
                ) {
                    return false;
                }
                return true;
            })
            .map((optData) => {
                const options = {
                    dataType: optData.dataType
                        ? optData.dataType === "list"
                            ? optData.elementType
                            : optData.dataType.toLowerCase()
                        : undefined,
                };
                return Object.assign({}, optData, options);
            });
    }

    _registerListeners() {
        if (
            !this._xDomeDeviceInclusionFilter ||
            typeof this._xDomeDeviceInclusionFilter.on !== "function"
        ) {
            return;
        }
        this._xDomeDeviceInclusionFilter.on("AdvancedFilterRuleChanged", (event) => {
            this._onInclusionFilterChanged(event, "xDome");
        });
    }

    _unRegisterListeners() {
        if (
            !this._xDomeDeviceInclusionFilter ||
            typeof this._xDomeDeviceInclusionFilter.removeListener !== "function"
        ) {
            return;
        }
        this._xDomeDeviceInclusionFilter.removeListener(
            "AdvancedFilterRuleChanged",
            this._onValueChanged
        );
    }

    _createNoConnectionPlaceholder() {
        return this._createBanner(
            "Unable to connect to Claroty xDome. Please verify configured connection details.",
            "no-cmdb-connection"
        );
    }

    _createNoMetaDataPlaceholder() {
        return this._createBanner(
            "Metadata could not be loaded for Claroty xDome.",
            "no-cmdb-connection"
        );
    }

    _createBanner(msg, className) {
        const msgContainer = document.createElement("div");
        msgContainer.classList.add(className);
        const innerSpan = document.createElement("span");
        innerSpan.innerText = msg;

        msgContainer.appendChild(innerSpan);

        return msgContainer;
    }

    _connectionFailed() {
        this._noConnectionEl.classList.add("show");
        this._tabMainContent.classList.add("no-connection");
    }

    _connectionPassed() {
        this._noConnectionEl.classList.remove("show");
        this._tabMainContent.classList.remove("no-connection");
        if (!this._xDomeDeviceInclusionFilter) {
            this._xDomeDeviceInclusionFilterEl.innerHTML = "";
            this._xDomeDeviceInclusionFilterEl.appendChild(
                this._createXDomeDeviceInclusionFilter()
            );
            if (!this._xDomeDeviceInclusionFilter) {
                this._tabMainContent.classList.add("no-connection");
            }
        }
        this._resetInclusionFilters();
    }

    /*******************************************/
    /* END PRIVATE METHODS                     */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _onInclusionFilterChanged(event) {
        let inclusionFilterRules, xdomeRules;

        try {
            inclusionFilterRules = event.builder.getRules({skip_empty: true, allow_invalid: true});
        } catch (error) {
            if (typeof error.message === "string") {
                const errorMessagesList = [
                    "Cannot read property 'each' of null".toLowerCase().replace(/['" ]/gi, ""),
                    "Cannot read property 'root' of null".toLowerCase().replace(/['" ]/gi, ""),
                    "Cannot read properties of null (reading 'each')"
                        .toLowerCase()
                        .replace(/['" ]/gi, ""),
                    "Cannot read properties of null (reading 'root')"
                        .toLowerCase()
                        .replace(/['" ]/gi, ""),
                ];
                if (
                    errorMessagesList.indexOf(error.message.toLowerCase().replace(/['" ]/gi, "")) <
                    0
                ) {
                    console.error(error);
                }
            }
        }

        if (_.get(inclusionFilterRules, "rules", []).length > 0) {
            xdomeRules = this.getXDomeRules(inclusionFilterRules);
            if (inclusionFilterRules.valid) {
                _.set(this._inclusionFilterQueries, "xDomeFilter", xdomeRules);

                this._xdDataSyncConfigEntity.setNamespaceMatchingCriteria(
                    Object.assign({}, this._xdDataSyncConfigEntity.getNamespaceMatchingCriteria(), {
                        ["xDomeFilter"]: xdomeRules,
                    })
                );
            }
        } else {
            _.set(this._inclusionFilterQueries, "xDomeFilter", {});

            const pristineNamespaceMatchingCriteria = _.get(
                this._xdDataSyncConfigEntity.getPristineProperties(),
                "namespaceMatchingCriteria"
            );
            const pristineSourceInclusionFilter = _.get(
                pristineNamespaceMatchingCriteria,
                "xDomeFilter"
            );
            let namespaceMatchingCriteria =
                this._xdDataSyncConfigEntity.getNamespaceMatchingCriteria();

            if (_.isEmpty(pristineSourceInclusionFilter)) {
                namespaceMatchingCriteria = Object.assign({}, namespaceMatchingCriteria, {
                    ["xDomeFilter"]: pristineSourceInclusionFilter,
                });
            } else {
                namespaceMatchingCriteria = Object.assign({}, namespaceMatchingCriteria, {
                    ["xDomeFilter"]: {},
                });
            }
            this._xdDataSyncConfigEntity.setNamespaceMatchingCriteria(namespaceMatchingCriteria);

            if (
                _.isEmpty(_.get(namespaceMatchingCriteria, "xDomeFilter")) &&
                _.isEmpty(pristineNamespaceMatchingCriteria)
            ) {
                this._xdDataSyncConfigEntity.setNamespaceMatchingCriteria(
                    pristineNamespaceMatchingCriteria
                );
            }
        }

        this.emit("ConfigChanged", this._inclusionFilterQueries);
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PUBLIC METHODS                    */
    /*******************************************/

    createFilterTab() {
        this._tabMainContent = document.createElement("div");
        this._tabMainContent.classList.add("content-main");
        this._xDomeDeviceInclusionFilterEl = document.createElement("div");
        this._xDomeDeviceInclusionFilterEl.appendChild(this._createXDomeDeviceInclusionFilter());
        const xDomeDeviceInclusionFilterSection = this._createSection(
            this._xDomeDeviceInclusionFilterEl,
            {
                title: "Claroty xDome Device Inclusion Filter",
                bordered: false,
            }
        );

        this._registerListeners();

        this._tabMainContent.appendChild(this._noMetaDataEl);
        this._tabMainContent.appendChild(this._noConnectionEl);
        this._tabMainContent.appendChild(xDomeDeviceInclusionFilterSection);

        this._setInclusionFilters(
            _.get(this._xdDataSyncConfigEntity, "pristineProperties.namespaceMatchingCriteria", {
                xDomeFilter: {},
            })
        );
    }

    getFilterTabContent() {
        return this._tabMainContent;
    }

    validateFilterTab() {
        let isValidXDomeInclusionFilter = false;
        let isEmptyXDomeInclusionFilter = false;

        if (!this._xDomeDeviceInclusionFilter) {
            return true;
        }

        try {
            isEmptyXDomeInclusionFilter =
                this._xDomeDeviceInclusionFilter._renderer._isQueryEmpty();

            isValidXDomeInclusionFilter =
                this._xDomeDeviceInclusionFilter._renderer.$builderContainer.queryBuilder(
                    "validate"
                );

            return isEmptyXDomeInclusionFilter || isValidXDomeInclusionFilter;
        } catch (error) {
            if (typeof error.message === "string") {
                const errorMessagesList = [
                    "Cannot read property 'each' of null".toLowerCase().replace(/['" ]/gi, ""),
                    "Cannot read property 'root' of null".toLowerCase().replace(/['" ]/gi, ""),
                    "Cannot read properties of null (reading 'each')"
                        .toLowerCase()
                        .replace(/['" ]/gi, ""),
                    "Cannot read properties of null (reading 'root')"
                        .toLowerCase()
                        .replace(/['" ]/gi, ""),
                ];
                if (
                    errorMessagesList.indexOf(error.message.toLowerCase().replace(/['" ]/gi, "")) <
                    0
                ) {
                    console.error(error);
                }
            }
        }
    }

    shutdown() {
        this._unRegisterListeners();

        this._customFieldsWithInherited = undefined;
        this._osMap = undefined;
        this._vendorMap = undefined;
        this._sitePathMap = undefined;
        this._skuList = undefined;
        this._deviceTypeBaseMap = undefined;
        this._metaData = undefined;
        this._inclusionFilterQueries = undefined;
        this._noConnectionEl = undefined;
        this._noMetaDataEl = undefined;

        const components = [this._tabMainContent, this._xDomeDeviceInclusionFilter];

        return Promise.all(components.map((component) => component.shutdown())).then(() => {
            components.forEach((component, index) => {
                components[index] = undefined;
            });
        });
    }

    /**
     * Converts rules to xDome formatted rules
     * @param {object} data - current rules by default
     * @returns {object}
     */
    getXDomeRules(data = this.getRules()) {
        if (!data) {
            return null;
        }

        const parse = (group) => {
            if (!group.rules || !_.isArray(group.rules)) {
                return {};
            }

            const parts = group.rules
                .map((rule) => {
                    if (_.isArray(rule.rules) && rule.rules.length > 0) {
                        return parse(rule);
                    } else {
                        return this._formatRuleForXDome(rule);
                    }
                })
                .filter(Boolean);

            return {
                operation: group.condition ? group.condition.toLowerCase() : "and",
                operands: parts,
            };
        };

        return parse(data);
    }

    /**
     * Support function to format a single rule for xDome
     * @param {object} rule
     * @returns {object}
     */
    _formatRuleForXDome(rule) {
        if (!rule) {
            return null;
        }
        return {
            field: rule.id,
            operation: glu_to_xd_operators[rule.operator] || rule.operator,
            value: rule.value,
        };
    }

    /**
     * Function to convert xDome formatted rules to standard rules
     * @param {object} xDomeRules
     * @returns {object}
     */
    _formatXDomeToStandard(xDomeRules) {
        if (!xDomeRules || !xDomeRules.operands) {
            return {
                condition: "AND",
                rules: [],
            };
        }
        const parse = (xDomeRules) => {
            const rules = xDomeRules.operands.map((operand) => {
                if (!operand) {
                    return null;
                }
                if (operand.operands) {
                    return parse(operand);
                } else {
                    // Handle in operator and change it to equal with or
                    if (
                        operand.operation === "in" &&
                        Array.isArray(operand.value) &&
                        operand.value.length > 1
                    ) {
                        const updatedRule = {
                            operation: "OR",
                            operands: operand.value.map((value) => ({
                                field: operand.field,
                                operation: "equals",
                                value: value,
                            })),
                        };
                        operand = updatedRule;
                        return parse(operand);
                    }
                    return {
                        id: operand.field,
                        operator:
                            operand.operation === "in"
                                ? "equal"
                                : xd_to_glu_operators[operand.operation] || operand.operation,
                        value: Array.isArray(operand.value) ? operand.value[0] : operand.value,
                        field: operand.field,
                        name: operand.field, // Assuming name is the same as field
                    };
                }
            });

            return {
                condition: xDomeRules.operation ? xDomeRules.operation.toUpperCase() : "AND",
                rules,
            };
        };

        return parse(xDomeRules);
    }

    setDataSyncEntity(ety) {
        this._xdDataSyncConfigEntity = ety;
    }

    /*******************************************/
    /* END PUBLIC METHODS                      */
    /*******************************************/
}

module.exports = Filter;
